/* eslint-disable react/boolean-prop-naming */
import React, {useCallback, useEffect} from 'react';

import Box from '@mui/material/Box';
import {ReactGrid} from '@silevis/reactgrid';
import {arrayOf, bool, func, number, shape, string} from 'prop-types';
import '@silevis/reactgrid/styles.scss';
import '../../styles/spreadsheet/index.scss';

import {FONT_FAMILIES} from '../../const';
import FrenchDateCellTemplate from './cell-templates/FrenchDateCellTemplate';
import FrenchNumberCellTemplate from './cell-templates/FrenchNumberCellTemplate';
import useDimensions from '../../hooks/dom/useDimensions';
import useSpreadsheets from '../../hooks/providers/useSpreadsheets';

const BaseReactGrid = React.forwardRef(
  (
    {
      id,
      rows = [],
      columns = [],
      stickyTopRows = 0,
      stickyLeftColumns = 0,
      containerClassName = '',
      containerStyle,
      onFocusLocationChanged,
      onContextMenu,
      canReorderRows,
      onRowsReordered,
      onCellsChanged,
      enableFillHandle = false,
      enableRowSelection = false,
      customCellTemplates,
      initialFocusLocation,
      shouldAutoCalculateColumnsWidth = true
    },
    ref
  ) => {
    // TODO ADD COLUMNS IDS IN ALL SPREADSHEETS CELLS SO THE calculateColumnWidths works.

    const [containerRef, {width: containerWidth}] = useDimensions();
    const {allColumnsWidth, setColumnWidth, setAllColumnsWidths} = useSpreadsheets();

    const handleColumnResize = useCallback((columnId, widthAfterResize) => {
      setColumnWidth(id, columnId, widthAfterResize);
    }, []);

    const gridColumns = allColumnsWidth[id] || columns;

    const calculateColumnWidths = () => {
      if (rows.length === 0) return;

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return;

      const MIN_COLUMN_WIDTH = 32;
      context.font = '14px InstrumentSans';

      return columns.map(col => {
        const maxContentWidth = Math.max(
          ...rows.map(row => {
            const cell = row.cells.find(c => c.columnId === col.columnId);
            const cellValue = cell?.text || '';

            // TODO verify that this works for all sheets ?
            // Header cells have a filter icon but this is not measured by context.measureText
            if (cell?.type === 'headerCell') {
              return context.measureText(cellValue).width + 16;
            }
            return context.measureText(cellValue).width;
          }),
          context.measureText(col.title || '').width,
          MIN_COLUMN_WIDTH
        );

        return {
          ...col,
          width: Math.ceil(maxContentWidth + 10) // Add padding
        };
      });
    };

    // This hooks aims to truncate width of the widest column when all columns do not fit in container
    useEffect(() => {
      if (!shouldAutoCalculateColumnsWidth) return;

      const totalColumnsWidth = gridColumns.reduce((accumulator, col) => {
        return accumulator + col.width;
      }, 0);

      if (totalColumnsWidth > containerWidth) {
        const exceedingWidth = totalColumnsWidth - containerWidth;
        const widestColumn = gridColumns.reduce((max, col) => (col.width > max.width ? col : max), gridColumns[0]);
        const widthAfterResize = widestColumn.width - exceedingWidth;
        setColumnWidth(id, widestColumn.columnId, widthAfterResize);
      }
    }, [containerWidth, gridColumns]);

    // This hooks makes the initial setting of columns widths
    useEffect(() => {
      const someColumnHasBeenResized = gridColumns.some(c => c.hasBeenResized);
      const shouldUseDefaultColumnsWidths = !shouldAutoCalculateColumnsWidth || (rows.length === 0 && !someColumnHasBeenResized);
      // When no rows in the sheet, we use the default columns widths
      if (shouldUseDefaultColumnsWidths) {
        const cols = columns.map(col => {
          return {...col, width: col.width};
        });
        return setAllColumnsWidths(id, cols);
      }

      // When there is rows in the sheet, and user has not yet set custom columns width, we calculate their width based on content
      if (rows.length > 0 && !someColumnHasBeenResized) {
        const columnsAutoCalculatedWidth = calculateColumnWidths();
        setAllColumnsWidths(id, columnsAutoCalculatedWidth);
      }
    }, [columns, rows]);

    const secondaryLightColor = `${import.meta.env.VITE_SECONDARY_COLOR}1C`;
    const primaryLightColor = `${import.meta.env.VITE_MAIN_COLOR}66`;

    return (
      <Box
        ref={containerRef}
        className={containerClassName}
        style={containerStyle}
        id="reactgrid"
        sx={{
          '& .header-cell, .rg-headerCell-cell': {
            overflow: 'visible',
            background: `${import.meta.env.VITE_MAIN_COLOR} !important`,
            color: ' white !important',
            fontFamily: FONT_FAMILIES.TITLE
          },
          '& .cell-header-budget-year': {
            background: import.meta.env.VITE_MAIN_COLOR
          },

          '& .cell-header-budget-ratio': {
            background: import.meta.env.VITE_SECONDARY_COLOR
          },
          '& .cell-header-budget-month': {
            background: primaryLightColor,
            color: 'white !important',
            fontFamily: FONT_FAMILIES.TITLE
          },
          '& .cell-ratio': {
            background: secondaryLightColor,
            justifyContent: 'center',
            fontFamily: FONT_FAMILIES.BODY
          }
        }}
      >
        {gridColumns.length > 0 ? (
          <ReactGrid
            enableRangeSelection
            ref={ref}
            rows={rows}
            columns={gridColumns}
            stickyTopRows={stickyTopRows}
            stickyLeftColumns={stickyLeftColumns}
            onFocusLocationChanged={onFocusLocationChanged}
            onContextMenu={onContextMenu}
            canReorderRows={canReorderRows}
            onRowsReordered={onRowsReordered}
            onCellsChanged={onCellsChanged}
            enableFillHandle={enableFillHandle}
            enableRowSelection={enableRowSelection}
            onColumnResized={handleColumnResize}
            initialFocusLocation={initialFocusLocation}
            customCellTemplates={{
              frenchNumber: new FrenchNumberCellTemplate(),
              frenchDate: new FrenchDateCellTemplate(),
              ...customCellTemplates
            }}
            labels={{
              copyLabel: 'Copier',
              pasteLabel: 'Coller',
              cutLabel: 'Couper'
            }}
          />
        ) : null}
      </Box>
    );
  }
);

BaseReactGrid.propTypes = {
  id: string.isRequired,
  containerClassName: string,
  containerStyle: shape({}),
  enableFillHandle: bool,
  enableRowSelection: bool,
  stickyTopRows: number,
  stickyLeftColumns: number,
  onFocusLocationChanged: func,
  onContextMenu: func,
  canReorderRows: func,
  onRowsReordered: func,
  onCellsChanged: func,
  customCellTemplates: shape({}),
  rows: arrayOf(shape({})),
  columns: arrayOf(shape({})),
  initialFocusLocation: shape({columnId: string.isRequired, rowId: string.isRequired}),
  shouldAutoCalculateColumnsWidth: bool
};

export default BaseReactGrid;
